import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getQmsCategoryApiService } from '../api/getQmsCategoryApiService';
import { patchQmsCategory } from '../api/patchQmsCategoryApiService';
import { QMS_CATEGORY_LABELS } from '../constants/labels';
import { QMS_CATEGORY_ERROR_MESSAGES } from '../errors/errorMessages';
import { useQmsTagSuggestions } from './useQmsTagSuggestions';

export interface EditQmsCategoryFormData {
  name: string;
  description: string;
  existingTags: string[];
  newTags: string[];
}

export interface EditQmsCategoryFormErrors {
  name?: string;
  description?: string;
}

const LIST_PATH = '/Kategorie';

const validateName = (value: string): string | undefined => {
  if (!value.trim()) {
    return QMS_CATEGORY_ERROR_MESSAGES.REQUIRED;
  }
  if (value.trim().length > 50) {
    return QMS_CATEGORY_ERROR_MESSAGES.NAME_TOO_LONG;
  }
  return undefined;
};

const validateDescription = (value: string): string | undefined => {
  if (!value.trim()) {
    return QMS_CATEGORY_ERROR_MESSAGES.REQUIRED;
  }
  if (value.trim().length > 500) {
    return QMS_CATEGORY_ERROR_MESSAGES.DESCRIPTION_TOO_LONG;
  }
  return undefined;
};

const sortedTags = (tags: string[]): string[] =>
  [...tags].map((t) => t.toLowerCase()).sort();

export const useEditQmsCategory = (categoryId: string) => {
  const navigate = useNavigate();
  const tagSuggestions = useQmsTagSuggestions();

  const [formData, setFormData] = useState<EditQmsCategoryFormData>({
    name: '',
    description: '',
    existingTags: [],
    newTags: [],
  });
  const [initialFormData, setInitialFormData] =
    useState<EditQmsCategoryFormData>({
      name: '',
      description: '',
      existingTags: [],
      newTags: [],
    });
  const [status, setStatus] = useState<'active' | 'inactive'>('active');
  const [initialStatus, setInitialStatus] = useState<'active' | 'inactive'>(
    'active'
  );

  const [errors, setErrors] = useState<EditQmsCategoryFormErrors>({});
  const [tagError, setTagError] = useState('');
  const hasPendingTagRef = useRef(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [accessError, setAccessError] = useState(false);

  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
  const [snackbarError, setSnackbarError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!categoryId) {
        setIsLoading(false);
        setNotFound(true);
        return;
      }
      setIsLoading(true);
      setNotFound(false);
      const response = await getQmsCategoryApiService.getCategory(categoryId);
      if (cancelled) {
        return;
      }
      if (response.success && response.data) {
        const cat = response.data;
        const tagNames = cat.tags.map((t) => t.name);
        const next: EditQmsCategoryFormData = {
          name: cat.name,
          description: cat.description,
          existingTags: tagNames,
          newTags: [],
        };
        setFormData(next);
        setInitialFormData(next);
        setStatus(cat.status);
        setInitialStatus(cat.status);
        setLoadError(null);
      } else if (response.forbidden) {
        setAccessError(true);
      } else if (response.notFound) {
        setNotFound(true);
      } else {
        setLoadError(
          response.error ?? QMS_CATEGORY_ERROR_MESSAGES.FETCH_CATEGORY_ERROR
        );
      }
      setIsLoading(false);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [categoryId]);

  const handleNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const next = e.target.value.trimStart().slice(0, 50);
      setFormData((prev) => ({ ...prev, name: next }));
      if (errors.name) {
        setErrors((prev) => {
          const { name: _n, ...rest } = prev;
          return rest;
        });
      }
    },
    [errors.name]
  );

  const handleDescriptionChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const next = e.target.value.trimStart().slice(0, 500);
      setFormData((prev) => ({ ...prev, description: next }));
      if (errors.description) {
        setErrors((prev) => {
          const { description: _d, ...rest } = prev;
          return rest;
        });
      }
    },
    [errors.description]
  );

  const handleTagsChange = useCallback(
    (existingTags: string[], newTags: string[]) => {
      setFormData((prev) => ({ ...prev, existingTags, newTags }));
    },
    []
  );

  const handleTagPendingChange = useCallback((hasPending: boolean) => {
    hasPendingTagRef.current = hasPending;
    if (!hasPending) {
      setTagError('');
    }
  }, []);

  const handleStatusToggle = useCallback(() => {
    setStatus((prev) => (prev === 'active' ? 'inactive' : 'active'));
  }, []);

  const hasChanges = useMemo(() => {
    if (formData.name.trim() !== initialFormData.name.trim()) {
      return true;
    }
    if (formData.description.trim() !== initialFormData.description.trim()) {
      return true;
    }
    if (status !== initialStatus) {
      return true;
    }
    const currentTags = sortedTags([
      ...formData.existingTags,
      ...formData.newTags,
    ]);
    const originalTags = sortedTags(initialFormData.existingTags);
    return JSON.stringify(currentTags) !== JSON.stringify(originalTags);
  }, [formData, initialFormData, status, initialStatus]);

  const applyFieldErrors = useCallback(
    (fieldErrors: Record<string, string>): boolean => {
      const mapped: EditQmsCategoryFormErrors = {};
      if (fieldErrors['name']) {
        mapped.name = fieldErrors['name'];
      }
      if (fieldErrors['description']) {
        mapped.description = fieldErrors['description'];
      }
      if (Object.keys(mapped).length > 0) {
        setErrors(mapped);
        return true;
      }
      return false;
    },
    []
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (hasPendingTagRef.current) {
        setTagError(QMS_CATEGORY_ERROR_MESSAGES.TAG_NOT_ADDED);
        return;
      }

      const nextErrors: EditQmsCategoryFormErrors = {};
      const nameError = validateName(formData.name);
      if (nameError) {
        nextErrors.name = nameError;
      }
      const descriptionError = validateDescription(formData.description);
      if (descriptionError) {
        nextErrors.description = descriptionError;
      }

      if (Object.keys(nextErrors).length > 0) {
        setErrors(nextErrors);
        return;
      }

      setIsSubmitting(true);
      try {
        const result = await patchQmsCategory(categoryId, {
          name: formData.name.trim(),
          description: formData.description.trim(),
          tags: [...formData.existingTags, ...formData.newTags],
          status,
        });

        if (result.success) {
          navigate(LIST_PATH, {
            state: {
              navigationSuccessMessage: QMS_CATEGORY_LABELS.EDIT_SUCCESS,
            },
          });
          return;
        }

        const handledAsFieldError = result.fieldErrors
          ? applyFieldErrors(result.fieldErrors)
          : false;

        if (!handledAsFieldError) {
          setSnackbarError(
            result.error ?? QMS_CATEGORY_ERROR_MESSAGES.UPDATE_ERROR
          );
          setIsSnackbarOpen(true);
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [formData, status, categoryId, applyFieldErrors, navigate]
  );

  const handleCancel = useCallback(() => {
    if (!hasChanges) {
      navigate(LIST_PATH);
      return;
    }
    setIsCancelModalOpen(true);
  }, [hasChanges, navigate]);

  const handleCloseCancelModal = useCallback(() => {
    setIsCancelModalOpen(false);
  }, []);

  const handleConfirmCancel = useCallback(() => {
    setIsCancelModalOpen(false);
    navigate(LIST_PATH);
  }, [navigate]);

  const handleCloseSnackbar = useCallback(() => {
    setIsSnackbarOpen(false);
  }, []);

  return {
    formData,
    status,
    errors,
    tagError,
    isLoading,
    isSubmitting,
    loadError,
    notFound,
    accessError,
    hasChanges,
    isCancelModalOpen,
    isSnackbarOpen,
    snackbarError,
    handleNameChange,
    handleDescriptionChange,
    handleTagsChange,
    handleTagPendingChange,
    handleStatusToggle,
    handleSubmit,
    handleCancel,
    handleCloseCancelModal,
    handleConfirmCancel,
    handleCloseSnackbar,
    tagSuggestions: tagSuggestions.suggestions,
    isTagSuggestionsLoading: tagSuggestions.isLoading,
    onTagSearchChange: tagSuggestions.setSearch,
  };
};
